import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, toast } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Plus, Bell, BellOff, Trash2, TrendingUp, TrendingDown,
  RefreshCw, Volume2, ArrowUp, ArrowDown, Percent
} from "lucide-react";
import SEOHead from "@/components/SEOHead";

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newAlert, setNewAlert] = useState({
    symbol: "",
    alert_type: "above",
    target_price: "",
    percent_change: "",
    note: ""
  });

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/alerts`);
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const createAlert = async () => {
    if (!newAlert.symbol) {
      toast.error("Please enter a symbol");
      return;
    }
    if (newAlert.alert_type !== "percent_change" && !newAlert.target_price) {
      toast.error("Please enter a target price");
      return;
    }
    if (newAlert.alert_type === "percent_change" && !newAlert.percent_change) {
      toast.error("Please enter percent change");
      return;
    }

    try {
      await axios.post(`${API}/alerts`, {
        symbol: newAlert.symbol.toUpperCase(),
        alert_type: newAlert.alert_type,
        target_price: newAlert.target_price ? parseFloat(newAlert.target_price) : null,
        percent_change: newAlert.percent_change ? parseFloat(newAlert.percent_change) : null,
        note: newAlert.note || null
      });
      toast.success("Alert created!");
      setShowCreateDialog(false);
      setNewAlert({
        symbol: "",
        alert_type: "above",
        target_price: "",
        percent_change: "",
        note: ""
      });
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create alert");
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`);
      toast.success("Alert deleted");
      fetchAlerts();
    } catch (error) {
      toast.error("Failed to delete alert");
    }
  };

  const toggleAlert = async (alertId, isActive) => {
    try {
      await axios.put(`${API}/alerts/${alertId}`, { is_active: !isActive });
      toast.success(isActive ? "Alert disabled" : "Alert enabled");
      fetchAlerts();
    } catch (error) {
      toast.error("Failed to update alert");
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case "above":
        return <ArrowUp className="w-5 h-5 text-green-600" />;
      case "below":
        return <ArrowDown className="w-5 h-5 text-red-600" />;
      case "percent_change":
        return <Percent className="w-5 h-5 text-blue-600" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#00b386]" />
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title="Price Alerts" 
        description="Set price alerts for stocks and get notified when targets are reached"
        path="/alerts"
      />
      <div className="space-y-6 pb-8 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Price Alerts</h1>
            <p className="text-gray-600 mt-1">Get notified when stocks reach your target prices</p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#00b386] hover:bg-[#009970] rounded-xl">
                <Plus className="w-4 h-4 mr-2" />
                Create Alert
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create Price Alert</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium">Symbol</label>
                  <Input
                    placeholder="e.g., RELIANCE"
                    value={newAlert.symbol}
                    onChange={(e) => setNewAlert({...newAlert, symbol: e.target.value.toUpperCase()})}
                  />
                </div>

                <div>
                  <label className="text-sm font-medium">Alert Type</label>
                  <Select
                    value={newAlert.alert_type}
                    onValueChange={(v) => setNewAlert({...newAlert, alert_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="above">Price Goes Above</SelectItem>
                      <SelectItem value="below">Price Goes Below</SelectItem>
                      <SelectItem value="percent_change">Percent Change</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {newAlert.alert_type !== "percent_change" ? (
                  <div>
                    <label className="text-sm font-medium">Target Price</label>
                    <Input
                      type="number"
                      placeholder="₹0.00"
                      value={newAlert.target_price}
                      onChange={(e) => setNewAlert({...newAlert, target_price: e.target.value})}
                    />
                  </div>
                ) : (
                  <div>
                    <label className="text-sm font-medium">Percent Change</label>
                    <Input
                      type="number"
                      placeholder="e.g., 5 for 5%"
                      value={newAlert.percent_change}
                      onChange={(e) => setNewAlert({...newAlert, percent_change: e.target.value})}
                    />
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium">Note (Optional)</label>
                  <Input
                    placeholder="Add a note..."
                    value={newAlert.note}
                    onChange={(e) => setNewAlert({...newAlert, note: e.target.value})}
                  />
                </div>

                <Button onClick={createAlert} className="w-full bg-[#00b386]">
                  Create Alert
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-green-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Alerts</p>
                  <p className="text-3xl font-bold text-green-600">
                    {alerts.filter(a => a.is_active).length}
                  </p>
                </div>
                <Bell className="w-10 h-10 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Triggered</p>
                  <p className="text-3xl font-bold text-blue-600">
                    {alerts.filter(a => a.triggered_at).length}
                  </p>
                </div>
                <Volume2 className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-gray-50 to-gray-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Alerts</p>
                  <p className="text-3xl font-bold text-gray-600">{alerts.length}</p>
                </div>
                <BellOff className="w-10 h-10 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts List */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle>Your Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No price alerts yet</p>
                <p className="text-sm">Create an alert to get notified when prices hit your target</p>
              </div>
            ) : (
              <div className="space-y-3">
                {alerts.map((alert) => (
                  <div 
                    key={alert.id}
                    className={`flex items-center justify-between p-4 rounded-xl transition-colors ${
                      alert.is_active ? "bg-gray-50 hover:bg-gray-100" : "bg-gray-100 opacity-60"
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        alert.alert_type === "above" ? "bg-green-100" : 
                        alert.alert_type === "below" ? "bg-red-100" : "bg-blue-100"
                      }`}>
                        {getAlertIcon(alert.alert_type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{alert.symbol}</p>
                          {alert.triggered_at && (
                            <Badge className="bg-purple-100 text-purple-700">Triggered</Badge>
                          )}
                          {!alert.is_active && (
                            <Badge variant="outline">Disabled</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {alert.alert_type === "above" && `Alert when price goes above ₹${alert.target_price}`}
                          {alert.alert_type === "below" && `Alert when price goes below ₹${alert.target_price}`}
                          {alert.alert_type === "percent_change" && `Alert on ${alert.percent_change}% change`}
                        </p>
                        {alert.note && (
                          <p className="text-xs text-gray-400 mt-1">{alert.note}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => toggleAlert(alert.id, alert.is_active)}
                        className="rounded-xl"
                      >
                        {alert.is_active ? (
                          <BellOff className="w-4 h-4" />
                        ) : (
                          <Bell className="w-4 h-4" />
                        )}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-500 hover:text-red-700 rounded-xl"
                        onClick={() => deleteAlert(alert.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
